import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
  acceptedPrivacy: false,
};

function Contact() {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const submitTimerRef = useRef(null);
  const successTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(submitTimerRef.current);
      window.clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => ({
        ...current,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Please enter your first name.";
    }

    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Please enter your last name.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }


    if (!formData.subject) {
      nextErrors.subject = "Please select a subject.";
    }

    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add a little more detail (at least 10 characters).";
    }

    if (!formData.acceptedPrivacy) {
      nextErrors.acceptedPrivacy = "Please acknowledge the privacy notice.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);

      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    window.clearTimeout(submitTimerRef.current);
    window.clearTimeout(successTimerRef.current);
    setIsSubmitting(true);
    setErrors({});

    submitTimerRef.current = window.setTimeout(() => {
      setFormData(emptyForm);
      setIsSubmitting(false);
      setShowSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });

      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
      }, 6000);
    }, 800);
  };

  return (
    <main className="contact">
      <header className="contact__header">
        <p className="contact__eyebrow">Customer care</p>
        <h1>Let’s talk.</h1>
        <p>
          Questions about an item, your order, or a future collaboration? Send
          us a note and our studio team will point you in the right direction.
        </p>
      </header>

      {showSuccess && (
        <div className="contact__success" role="status" aria-live="polite">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Your note is ready.</strong>
            <p>
              Thank you. Your enquiry has been prepared successfully.
            </p>
          </div>
          <button type="button" onClick={() => setShowSuccess(false)} aria-label="Dismiss message">
            ×
          </button>
        </div>
      )}

      <div className="contact__layout">
        <aside className="contact__details" aria-label="Contact information">
          <div>
            <p className="contact__eyebrow">Get in touch</p>
            <h2>We’re here to help.</h2>
          </div>

          <dl>
            <div>
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              </dd>
            </div>
            <div>
              <dt>Studio</dt>
              <dd>{siteConfig.contact.address}</dd>
            </div>
            <div>
              <dt>Hours</dt>
              <dd>{siteConfig.contact.hours}</dd>
            </div>
          </dl>

          <p className="contact__aside-note">
            Looking for delivery or return details? Visit our{" "}
            <Link to="/shipping">shipping guide</Link> or{" "}
            <Link to="/returns">returns page</Link>.
          </p>
        </aside>

        <section className="contact__form-panel" aria-labelledby="contact-form-title">
          <div className="contact__form-heading">
            <p className="contact__eyebrow">Send an enquiry</p>
            <h2 id="contact-form-title">How can we help?</h2>
            <p>Fields marked with an asterisk are required.</p>
          </div>

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="contact__field-grid">
              <div className={`contact__field ${errors.firstName ? "contact__field--error" : ""}`}>
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={errors.firstName ? "firstName-error" : undefined}
                />
                {errors.firstName && <span id="firstName-error">{errors.firstName}</span>}
              </div>

              <div className={`contact__field ${errors.lastName ? "contact__field--error" : ""}`}>
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={errors.lastName ? "lastName-error" : undefined}
                />
                {errors.lastName && <span id="lastName-error">{errors.lastName}</span>}
              </div>
            </div>

            <div className="contact__field-grid">
              <div className={`contact__field ${errors.email ? "contact__field--error" : ""}`}>
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && <span id="email-error">{errors.email}</span>}
              </div>

            </div>

            <div className={`contact__field ${errors.subject ? "contact__field--error" : ""}`}>
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="">Choose a topic</option>
                <option value="order">Order support</option>
                <option value="product">Product advice</option>
                <option value="returns">Returns and exchanges</option>
                <option value="press">Press and partnerships</option>
                <option value="wholesale">Wholesale and corporate gifting</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && <span id="subject-error">{errors.subject}</span>}
            </div>

            <div className={`contact__field ${errors.message ? "contact__field--error" : ""}`}>
              <div className="contact__label-row">
                <label htmlFor="message">Message *</label>
                <span>{formData.message.length}/800</span>
              </div>
              <textarea
                id="message"
                name="message"
                rows="7"
                maxLength="800"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
              />
              {errors.message && <span id="message-error">{errors.message}</span>}
            </div>

            <div className={`contact__consent ${errors.acceptedPrivacy ? "contact__consent--error" : ""}`}>
              <input
                id="acceptedPrivacy"
                name="acceptedPrivacy"
                type="checkbox"
                checked={formData.acceptedPrivacy}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.acceptedPrivacy)}
                aria-describedby={errors.acceptedPrivacy ? "acceptedPrivacy-error" : undefined}
              />
              <label htmlFor="acceptedPrivacy">
                I acknowledge the <Link to="/privacy">privacy notice</Link>,
                including how my enquiry information is handled. *
              </label>
              {errors.acceptedPrivacy && (
                <span id="acceptedPrivacy-error">{errors.acceptedPrivacy}</span>
              )}
            </div>

            <button className="contact__submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="contact__spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                "Send enquiry"
              )}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
